/**
 * PostDeleteGapFillModal
 * ----------------------
 * iter1382 · Auto-opened after a manual statement delete that left a gap
 * the OB feed had continued past. Offers TWO paths to fill the hole:
 *
 *   a. Upload a bank statement covering the missing period. This goes
 *      through the SAME LLM parser + continuity guard as any other
 *      upload; the guard verifies balance continuity on both sides
 *      before committing.
 *
 *   b. Restore from Mono/Plaid. Replays stored open-banking rows
 *      (`mono_transactions` / `plaid_transactions`) that were skipped
 *      when the manual statement covered the same period. The backend
 *      re-runs the continuity check; if the replay would still leave a
 *      hole (some OB rows were never fetched) it returns an error and
 *      the user is asked to upload the statement instead.
 *
 * The user can also close the modal ("Do it later") — a Fill Gap chip
 * remains on the account tile so they can reopen it whenever.
 */
import React, { useState } from 'react';
import apiClient from '../../utils/apiClient';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Upload, RefreshCw, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export const PostDeleteGapFillModal = ({
  open, onOpenChange, bankAccountId, gapStart, gapEnd, onManualUploadClick, reason = 'deleted_statement', connected = true, feedCheck = null, partial = null,
}) => {
  const movedStart = reason === 'start_date_moved';
  const providerLimit = reason === 'provider_history_limit';
  const noticeGap = movedStart || providerLimit;
  // Start-date move-back / provider-limit gaps are manual-upload only (bank-feed replay withdrawn 9 Oct 2026).
  const showRestore = connected && !noticeGap;
  const [openedOn, setOpenedOn] = useState('');
  const [openedBusy, setOpenedBusy] = useState(false);
  const declareOpenedLater = async () => {
    if (!openedOn) return;
    setOpenedBusy(true);
    try {
      const { data } = await apiClient.post('/bank-feeds/gap-fill/account-opened-later', { bank_account_id: bankAccountId, opened_on: openedOn });
      if (data?.ok) { toast.success('Noted — opening balance set to 0 and this gap is closed. No statement is needed for it.'); onOpenChange(false); }
      else toast.error(data?.reason || 'Could not record the opening date');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the opening date'); }
    finally { setOpenedBusy(false); }
  };
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const restoreFromOB = async () => {
    if (!bankAccountId) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await apiClient.post(
        '/bank-feeds/gap-fill/from-open-banking',
        {
          bank_account_id: bankAccountId,
          gap_start: gapStart,
          gap_end: gapEnd,
        },
      );
      setResult(data);
      if (data.ok) {
        toast.success(`Restored ${data.rows_inserted || 0} transaction(s) from bank feed.`);
      } else {
        toast.error(data.reason || 'Restore failed — please upload a statement instead.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Restore failed: ${detail}`);
      setResult({ ok: false, reason: detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="post-delete-gap-fill-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            {movedStart ? 'Statement needed for your new start date' : providerLimit ? 'Your bank connection could not reach your start date' : 'Fill the gap you just created'}
          </DialogTitle>
          <DialogDescription>
            {movedStart ? (
              <>Your accounting start date moved back, so this account needs a statement covering the period below
                before its opening balance can be placed. Upload it now, or later — we&apos;ll keep a Fill Gap chip on the account tile.</>
            ) : providerLimit ? (
              <>Your bank connection has no transactions for this account between your accounting start date and its first fetched
                transaction. If the account was only opened after {gapStart}, tell us the opening date below — otherwise upload a statement
                covering the period so the opening balance can be placed.</>
            ) : (
              <>The deleted statement covered transactions your bank feed has
                already continued past. Pick one of the two ways below to
                fill the hole. You can also do it later — we&apos;ll leave a
                Fill Gap chip on your account tile.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-xs text-slate-700">
          {(gapStart || gapEnd) && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="gap-fill-window">
              Gap window:{' '}
              <strong>{gapStart || '?'}</strong> →{' '}
              <strong>{gapEnd || '?'}</strong>
              {partial?.covered_through && (
                <div className="mt-1 text-[11px] text-amber-800" data-testid="gap-fill-partial-note">
                  A statement already covered {partial.original_gap_start || 'the start'} → {partial.covered_through}, but its closing balance
                  {partial.join_check?.statement_close != null ? ` (${Number(partial.join_check.statement_close).toLocaleString()})` : ''} does not match the
                  balance your books open with on {partial.join_check?.next_row_date}
                  {partial.join_check?.next_row_open != null ? ` (${Number(partial.join_check.next_row_open).toLocaleString()})` : ''} — transactions in this window are still missing.
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => { onOpenChange(false); onManualUploadClick?.(bankAccountId); }}
            disabled={busy}
            className="w-full text-left rounded-lg border border-slate-200 hover:border-[#108981] hover:bg-slate-50 p-3 transition-colors disabled:opacity-60"
            data-testid="gap-fill-manual-upload-btn"
          >
            <div className="flex items-start gap-3">
              <Upload className="w-5 h-5 text-[#108981] shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900 text-[13px]">
                  Upload a bank statement (recommended)
                </p>
                <p className="text-slate-600 mt-0.5">
                  Same AI parser + continuity check as any other upload.
                  Works even for banks not connected to open banking.
                </p>
              </div>
            </div>
          </button>

          {connected && noticeGap && (
            <div className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-200 rounded text-slate-700 text-xs" data-testid="gap-fill-manual-only-note">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
              <span>{movedStart ? 'Because your start date moved back, this gap is filled from a bank statement only — the bank feed isn\'t used for it.'
                : 'Your bank connection has already given us everything it holds for this account — this period can only come from a bank statement.'}</span>
            </div>
          )}
          {showRestore && <button
            type="button"
            onClick={restoreFromOB}
            disabled={busy}
            className="w-full text-left rounded-lg border border-slate-200 hover:border-[#108981] hover:bg-slate-50 p-3 transition-colors disabled:opacity-60"
            data-testid="gap-fill-ob-restore-btn"
          >
            <div className="flex items-start gap-3">
              {busy ? (
                <Loader2 className="w-5 h-5 text-[#108981] shrink-0 mt-0.5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5 text-[#108981] shrink-0 mt-0.5" />
              )}
              <div>
                <p className="font-semibold text-slate-900 text-[13px]">
                  Restore from bank feed
                </p>
                <p className="text-slate-600 mt-0.5">
                  Replay transactions we already fetched from your bank
                  connection but held back during your manual upload. If
                  any are missing we&apos;ll ask you to upload a statement
                  instead.
                </p>
              </div>
            </div>
          </button>}

          {noticeGap && (
            <div className={`rounded-lg border p-3 space-y-2 ${providerLimit ? 'border-[#108981]/50 bg-emerald-50/40' : 'border-slate-200'}`} data-testid="gap-fill-opened-later">
              <p className="text-sm font-medium text-slate-800">{providerLimit ? `Was this account opened after ${gapStart}?` : `Did this account not exist on ${gapStart}?`}</p>
              <p className="text-xs text-slate-600">Tell us when it was opened: the opening balance becomes 0, this gap closes and no statement is needed for it.</p>
              <label className="block text-xs font-medium text-slate-700" htmlFor="gap-fill-opened-on">Date the account was opened</label>
              <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[180px]">
                  {!openedOn && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">Tap to pick a date</span>}
                  <input id="gap-fill-opened-on" type="date" value={openedOn} onChange={(e) => setOpenedOn(e.target.value)} min={gapStart}
                    className={`w-full min-h-[38px] border rounded px-2 py-1.5 text-sm bg-white text-slate-900 ${openedOn ? '' : '[&::-webkit-datetime-edit]:text-transparent'}`} data-testid="gap-fill-opened-on-input" />
                </div>
                <Button size="sm" variant="outline" disabled={!openedOn || openedBusy} onClick={declareOpenedLater} data-testid="gap-fill-opened-later-btn">
                  {openedOn ? `Confirm — opened on ${openedOn}` : 'Confirm opening date'}
                </Button>
              </div>
            </div>
          )}
          {result && !result.ok && (
            <div
              className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-900"
              data-testid="gap-fill-restore-error"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{result.reason}</span>
            </div>
          )}
          {result && result.ok && (
            <div
              className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded text-green-900"
              data-testid="gap-fill-restore-success"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Restored {result.rows_inserted || 0} transaction(s) from the
                bank feed{result.opening_balance != null ? ` · opening balance ${Number(result.opening_balance).toLocaleString()}` : ''}. Continuity verified.
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="gap-fill-later-btn"
          >
            Do it later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostDeleteGapFillModal;
